import { ChildProps } from "../../../types/types";
export const slugify = (str: string) => {
  return str
    ?.toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, '')
    .replace(/[\s_-]+/g, '-')
    .replace(/^-+|-+$/g, '');
};

export const getNestedFieldValue = (obj: any, fieldPath: string) => {
  const modifiedFieldPath = fieldPath?.replace("chatCompany", "company")?.replace("chat.", "cxItem.");
  const fieldParts = modifiedFieldPath?.split(".");
  return fieldParts?.reduce((o, part) => (o ? o[part] : null), obj);
};

const checkCondition = (request: any, condition: Record<string, any>) => {
  if (!condition || Object.keys(condition).length === 0) return true;
  for (const [fieldPath, expectedValue] of Object.entries(condition)) {
    const modifiedFieldPath = fieldPath.replace("chatCompany", "company");
    const fieldValue = getNestedFieldValue(request, modifiedFieldPath);
    const isNotOperator = typeof expectedValue === "string" && expectedValue.startsWith("!");
    const comparisonValue = isNotOperator ? expectedValue.slice(1) : expectedValue;
    if (isNotOperator) {
      return fieldValue !== comparisonValue;
    } else {
      if (fieldValue !== comparisonValue) {
        return false;
      }
    }
  }
  return true;
};

const extractNumber = (value: any): number | null => {
  if (typeof value === "number") {
    return value;
  }
  if (typeof value === "string") {
    const numericString = value.replace(/[^0-9.-]+/g, "");
    const parsed = parseFloat(numericString);
    return isNaN(parsed) ? null : parsed;
  }
  return null;
};

export type RankData = {
  index: number;
  total: number;
  objectName: string;
  childName: string;
};

/* NEW: GroupBy helper function */
const calculateGroupByResults = (
  items: any[],
  groupByField: string,
  op: string,
  fieldPath: string,
  defaultValue: any
): Record<string, any> => {
  const groupedResult: Record<string, any> = {};
  for (const item of items) {
    let groupValue = getNestedFieldValue(item, groupByField);
    if (groupValue === null || groupValue === undefined) continue;
    groupValue = slugify(String(groupValue));
    if (!groupedResult[groupValue]) {
      groupedResult[groupValue] = {
        amount: 0,
        count: 0,
        min: null as number | null,
        max: null as number | null,
        values: [] as any[],
        accounts: new Set<string>(),
      };
    }
    const fieldValue = getNestedFieldValue(item, fieldPath);
    const value = fieldValue != null ? extractNumber(fieldValue) : defaultValue || 0;
    switch (op) {
    case "sum":
      if (typeof value === "number") {
        groupedResult[groupValue].amount += value;
      }
      break;
    case "count":
      groupedResult[groupValue].count += 1;
      break;
    case "avg":
      if (typeof value === "number") {
        groupedResult[groupValue].amount += value;
        groupedResult[groupValue].count += 1;
      }
      break;
    case "min":
      if (typeof value === "number") {
        if (groupedResult[groupValue].min === null || value < groupedResult[groupValue].min!) {
          groupedResult[groupValue].min = value;
        }
      }
      break;
    case "max":
      if (typeof value === "number") {
        if (groupedResult[groupValue].max === null || value > groupedResult[groupValue].max!) {
          groupedResult[groupValue].max = value;
        }
      }
      break;
    case "concat":
      if (value !== null && value !== undefined) {
        groupedResult[groupValue].values.push(value);
      }
      break;
    default:
      break;
    }
    const orgId = getNestedFieldValue(item, "orgId");
    const companyId = getNestedFieldValue(item, "company._id");
    const accountId = orgId || companyId;
    if (accountId && (accountId?.toLowerCase?.() !== "n/a")) {
      groupedResult[groupValue].accounts.add(String(accountId));
    }
  }
  const finalResult: Record<string, any> = {};
  for (const group in groupedResult) {
    const data = groupedResult[group];
    finalResult[group] = {};
    switch (op) {
    case "sum":
      finalResult[group].amount = data.amount;
      break;
    case "count":
      finalResult[group].count = data.count;
      break;
    case "avg":
      finalResult[group].avg = data.count > 0 ? data.amount / data.count : 0;
      break;
    case "min":
      finalResult[group].min = data.min;
      break;
    case "max":
      finalResult[group].max = data.max;
      break;
    case "concat":
      finalResult[group].values = data.values;
      break;
    default:
      break;
    }
    finalResult[group].accounts = data.accounts.size;
  }
  return finalResult;
};

export const calculateImpactResults = (
  validated: any[],
  pending: any[],
  impactAggregationProperties: any[],
  idea: any
) => {
  const impactResults: Record<string, any> = {};


  impactAggregationProperties.forEach((property: any) => {
    const {
      fieldPath,
      propertyName,
      impact,
      fieldDefaultValue,
      operation = "sum",
      groupBy,
      consolidateBy,
    } = property;

    const displayRanks = impact.displayRanks || [];
    let ranksData: RankData[] | null = null;

    if (groupBy) {
      const totalGrouped = calculateGroupByResults(
        [...validated, ...pending],
        groupBy,
        operation,
        fieldPath,
        fieldDefaultValue
      );
      impactResults[propertyName] = {
        total: totalGrouped,
        ranksData: displayRanks
          ?.map((rankPropertyName: string) => {
            const objectName = rankPropertyName;
            let childName = null;
            let rankData = null;
            if (["domains", "components"].includes(objectName)) {
              if (objectName == "components") {
                childName = idea.componentObj?.name;
                rankData = idea.ranks?.components?.[childName.toLowerCase()];
              } else {
                childName = idea.componentObj?.domain;
                rankData = idea.ranks?.domains?.[childName.toLowerCase()];
              }
            } else if (idea.ranks?.[objectName]) {
              rankData = idea.ranks?.[objectName];
            } else {
              return undefined;
            }
            return {
              index: rankData?.index,
              total: rankData?.total,
              objectName,
              childName,
            };
          })
          .filter((rank: any) => rank !== undefined),
      };
      return;
    }

    let total: number | string = 0;
    let validatedTotal: number | string = 0;
    const pendingCompanyIds: Set<string> = new Set();
    const validatedCompanyIds: Set<string> = new Set();
    const pendingConsolidationIds: Set<string> = new Set();
    const validatedConsolidationIds: Set<string> = new Set();

    // Support for flexible consolidateBy field, fallback to legacy consolidateByCompany
    const consolidateByField = consolidateBy || (impact?.consolidateByCompany ? "company._id" : null);
    const multiplyByComponentWeight = impact?.multiplyByComponentWeight || false;
    


    const pendingConsolidatedValues: Record<string, number[]> = {};
    const validatedConsolidatedValues: Record<string, number[]> = {};

    const processedChatIds = new Set<string>();

    const applyOperation = (values: number[]) => {
      if (values.length === 0) return 0;
      switch (operation) {
      case "concat":
        return values.join(", ");
      case "sum":
        return values.reduce((acc, val) => acc + val, 0);
      case "count":
        return values.length;
      case "avg":
        return values.reduce((acc, val) => acc + val, 0) / values.length;
      case "min":
        return Math.min(...values);
      case "max":
        return Math.max(...values);
      default:
        return 0;
      }
    };

    // Use prefix match (matches parseDBFieldPath in PropertyManager) so that
    // attribute names happening to contain "cxItem"/"opportunity" as a
    // substring (e.g. cxItem.opportunityType) don't accidentally trigger the
    // wrong dedup bucket and silently skip values.
    const shouldConsolidateByChat = fieldPath.startsWith("cxItem.");
    // Opportunity-prefixed properties dedup independently of cxItem to avoid
    // double-counting when an evidence is linked to both.
    const shouldConsolidateByOpportunity = fieldPath.startsWith("opportunity.");
    const processedOpportunityIds = new Set<string>();

    const processRequest = (
      request: any,
      consolidatedValues: Record<string, number[]>,
      companyIds: Set<string>,
      consolidationIds: Set<string>,
      isValidated: boolean,
      defaultValue: number
    ) => {
      const fieldValue = getNestedFieldValue(request, fieldPath);
      
      // More conservative default value application - only apply if the parent object exists
      let value;
      if (fieldValue != null) {
        value = extractNumber(fieldValue);
      } else if (defaultValue && fieldPath.includes('.')) {
        // Check if parent object exists before applying default
        const pathParts = fieldPath.split('.');
        const parentPath = pathParts.slice(0, -1).join('.');
        const parentObject = getNestedFieldValue(request, parentPath);
        value = parentObject ? (defaultValue || 0) : 0;
      } else {
        value = defaultValue || 0;
      }
      const companyId = getNestedFieldValue(request, "company._id");
      const cxItemId = getNestedFieldValue(request, "cxItem._id");
      const opportunityId = getNestedFieldValue(request, "opportunity._id");
      


      if (value && !isNaN(value)) {
        if (shouldConsolidateByChat && cxItemId) {
          if (processedChatIds.has(cxItemId)) {
            return;
          }
          processedChatIds.add(cxItemId);
        }
        if (shouldConsolidateByOpportunity && opportunityId) {
          if (processedOpportunityIds.has(opportunityId)) {
            return;
          }
          processedOpportunityIds.add(opportunityId);
        }

        if (consolidateByField) {
          // Get the consolidation key using the flexible consolidateBy field
          const consolidationKey = getNestedFieldValue(request, consolidateByField);
          if (consolidationKey && consolidationKey !== "N/A") {
            if (!consolidatedValues[consolidationKey]) {
              consolidatedValues[consolidationKey] = [];
            }
            consolidatedValues[consolidationKey].push(value);
            consolidationIds.add(consolidationKey);
          }
        } else {
          if (isValidated) {
            //@ts-ignore
            validatedTotal += value;
          } else {
            //@ts-ignore
            total += value;
          }
        }
        if (companyId && companyId !== "N/A") companyIds.add(companyId);
      }
    };

    validated.forEach((request) =>
      processRequest(
        request,
        validatedConsolidatedValues,
        validatedCompanyIds,
        validatedConsolidationIds,
        true,
        fieldDefaultValue
      )
    );

    pending.forEach((request) =>
      processRequest(
        request,
        pendingConsolidatedValues,
        pendingCompanyIds,
        pendingConsolidationIds,
        false,
        fieldDefaultValue
      )
    );

    if (consolidateByField) {
      // For consolidation, we need to count unique keys or apply operation to consolidated values
      if (operation === "count") {
        // Count unique consolidation keys
        validatedTotal = Object.keys(validatedConsolidatedValues).length;
        total = Object.keys(pendingConsolidatedValues).length;
      } else {
        // Apply operation to consolidated values (one per unique key)
        const validatedConsolidatedResults = Object.values(validatedConsolidatedValues).map(values => applyOperation(values));
        const pendingConsolidatedResults = Object.values(pendingConsolidatedValues).map(values => applyOperation(values));
        

        
        validatedTotal = applyOperation(validatedConsolidatedResults);
        total = applyOperation(pendingConsolidatedResults);
      }
    }

    const componentWeight = idea.componentObj?.weight || 1;
    if (multiplyByComponentWeight) {
      if (typeof total === "number") {
        total *= componentWeight;
      }
      if (typeof validatedTotal === "number") {
        validatedTotal *= componentWeight;
      }
    }

    ranksData = displayRanks?.map((rankPropertyName: string) => {
      const objectName = rankPropertyName;
      let childName = null;
      let rankData = null;
      if (["domains", "components"].includes(objectName)) {
        if (objectName == "components") {
          childName = idea.componentObj?.name;
          rankData = idea.ranks?.components?.[childName.toLowerCase()];
        } else {
          childName = idea.componentObj?.domain;
          rankData = idea.ranks?.domains?.[childName.toLowerCase()];
        }
      } else if (idea.ranks?.[objectName]) {
        rankData = idea.ranks?.[objectName];
      } else {
        return undefined;
      }
      return {
        index: rankData?.index,
        total: rankData?.total,
        objectName,
        childName,
      };
    });


    // Calculate customer counts based on consolidation
    let customerCount, validatedCustomerCount, totalCustomerCount;

    if (consolidateByField) {
      if (consolidateByField === "company._id") {
        // When consolidating by company, use company IDs
        customerCount = pendingCompanyIds.size;
        validatedCustomerCount = validatedCompanyIds.size;
        
        // Total unique companies = union of validated and pending company IDs
        const allCompanyIds = new Set([...validatedCompanyIds, ...pendingCompanyIds]);
        totalCustomerCount = allCompanyIds.size;
      } else {
        // When consolidating by other fields (like orgId), use consolidation IDs
        customerCount = pendingConsolidationIds.size;
        validatedCustomerCount = validatedConsolidationIds.size;
        
        // Total unique consolidation IDs = union of validated and pending consolidation IDs
        const allConsolidationIds = new Set([...validatedConsolidationIds, ...pendingConsolidationIds]);
        totalCustomerCount = allConsolidationIds.size;
      }
    } else {
      // No consolidation - use company IDs as fallback
      customerCount = pendingCompanyIds.size;
      validatedCustomerCount = validatedCompanyIds.size;
      const allCompanyIds = new Set([...validatedCompanyIds, ...pendingCompanyIds]);
      totalCustomerCount = allCompanyIds.size;
    }

    impactResults[propertyName] = {
      total,
      validatedTotal,
      customerCount,
      validatedCustomerCount,
      totalCustomerCount, // Add this new field for total unique customers
      ranksData: ranksData?.filter((rank) => rank !== undefined),
    };
  });

  return impactResults;
};